
import CursosDiplomasTabla from "@/components/cursos/TablaCursosDiplomas.vue";
import CursosInscripcionesTabla from "@/components/cursos/TablaCursosInscripciones.vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Page from "@/layout/Page.vue";
import { CursosModule } from "@/store/modules";
import { ArrowDown, Link } from "@element-plus/icons-vue";
import { QuillEditor } from "@vueup/vue-quill";
import { useClipboard } from "@vueuse/core";
import { ElMessage, UploadProps } from "element-plus";
import "moment/locale/es";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField, useForm } from "vee-validate";
import {
  computed,
  defineComponent,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import * as Yup from "yup";

interface CursoEditar {
  id?: string;
  nombre: string;
  nexpediente: string;
  descripcionCorta: string;
  descripcion: string;
  imagen: string;
  inicio: Date;
  fin: Date;
  horario: string;
  inicioInscripciones: Date;
  finInscripciones: Date;
  plazas: number;
  nhoras: number;
  publicado: boolean;
  presencial: boolean;
  videoconferencia: boolean;
  areaTematica?: string | null;
  modeloCertificado?: string | null;
  informacionParaElCertificado?: string | null;
  personalDocente?: string | null;
}

const formularioBaseUrl = process.env.VUE_APP_FORMULARIOS_URL;

export default defineComponent({
  name: "curso-detalle",
  components: {
    Page,
    ErrorMessage,
    ArrowDown,
    CursosInscripcionesTabla,
    CursosDiplomasTabla,
    QuillEditor,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const token = route.params.id;
    const isCreate = !token;
    const store = useStore();
    const module = getModule(CursosModule, store);

    const cargaDetalle = ref(false);
    const cargaListadoProfesionales = ref(false);
    const crearCurso = ref(false);
    const actualizarCurso = ref(false);
    const imagenUrl = ref("media/logos/logo_iam.png");
    const imagenBase64 = ref<string | null>(null);

    onBeforeMount(async () => {
      cargaDetalle.value = true;
      try {
        if (!isCreate) {
          await module.fetchById({
            id: token as string,
          });
        }
      } catch (err) {
        Swal.fire({
          text: t("formularios.cursoDetalle.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaDetalle.value = false;
      }
    });

    const datosCurso = computed(() => {
      return module.curso;
    });
    const inscripciones = computed(() => {
      return (
        module.curso?.inscripciones
          ?.map((ele) => {
            return {
              ...ele,
            };
          })
          .sort(function (a, b) {
            return b.createdOn > a.createdOn ? 1 : 0;
          }) ?? []
      );
    });
    /*
    const encuestas = computed(() => {
      return module.servicio?.servicio?.respuestasEncuesta ?? [];
    });
*/

    const url = computed(() => {
      if (!datosCurso.value?.formularioId) return "";
      return `${formularioBaseUrl}#/home?act=${datosCurso.value.formularioId}`;
    });

    const { text, copy, copied, isSupported } = useClipboard({ source: url });

    watch(datosCurso, () => {
      if (!datosCurso.value) return;

      resetForm({
        values: {
          nombre: datosCurso.value.nombre,
          nexpediente: datosCurso.value.nexpediente,
          descripcionCorta: datosCurso.value.descripcionCorta,
          descripcion: datosCurso.value.descripcion,
          imagen: datosCurso.value.imagen,
          inicio: datosCurso.value.inicio,
          fin: datosCurso.value.fin,
          horario: datosCurso.value.horario,
          inicioInscripciones: datosCurso.value.inicioInscripciones,
          finInscripciones: datosCurso.value.finInscripciones,
          plazas: datosCurso.value.plazas,
          nhoras: datosCurso.value.nhoras,
          publicado: datosCurso.value.publicado,
          presencial: datosCurso.value.presencial,
          videoconferencia: datosCurso.value.videoconferencia,
          areaTematica: datosCurso.value.areaTematica,
          modeloCertificado: datosCurso.value.modeloCertificado,
          informacionParaElCertificado:
            datosCurso.value.informacionParaElCertificado,
          personalDocente: datosCurso.value.personalDocente,
        },
      });
      imagenUrl.value = datosCurso.value.imagen;

      setCurrentPageBreadcrumbs(datosCurso.value?.nombre ?? "", [
        {
          title: t("menu.cursos"),
          path: "/listados/cursos",
        },
      ]);
    });

    const error = computed(() => module.errors);
    watch(error, () => {
      if (!error.value) return;

      Swal.fire({
        text:
          t("errors." + (error.value.error.code ?? error.value.error.name)) ??
          error.value.error.name,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: t("dialogs.okButton"),
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    });

    onMounted(() => {
      if (isCreate) {
        setCurrentPageBreadcrumbs(t("menu.nuevo"), [
          t("menu.cursos"),
          t("menu.cursos"),
        ]);

        const convertImageToBase64 = async (url: string): Promise<void> => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();

            const reader = new FileReader();
            reader.onloadend = () => {
              imagenBase64.value = reader.result as string;
            };
            reader.onerror = (error) => {
              console.error("Error al leer la imagen:", error);
            };

            reader.readAsDataURL(blob);
          } catch (error) {
            console.error("Error al convertir la imagen a Base64:", error);
          }
        };

        convertImageToBase64(imagenUrl.value);

        resetForm({
          values: {
            nombre: "",
            nexpediente: "",
            descripcionCorta: "",
            descripcion: "",
            imagen: imagenUrl.value,
            inicio: new Date(),
            fin: new Date(),
            horario: "",
            inicioInscripciones: new Date(),
            finInscripciones: new Date(),
            plazas: 50,
            nhoras: 2,
            publicado: false,
            presencial: true,
            videoconferencia: true,
            areaTematica: "",
            modeloCertificado: "",
            informacionParaElCertificado: "",
            personalDocente: "",
          },
        });
      }
    });

    const schema = Yup.object().shape({
      id: Yup.string(),
      nombre: Yup.string().required("El nombre es obligatorio"),
      nexpediente: Yup.string().required(
        "El número de expediente es obligatorio"
      ),
      descripcionCorta: Yup.string().required(
        "La descripción corta es obligatoria"
      ),
      descripcion: Yup.string().required("La descripción es obligatoria"),
      imagen: Yup.string().required("La imagen es obligatoria"),
      inicio: Yup.date()
        .typeError("La fecha de inicio es obligatoria")
        .required("La fecha de inicio es obligatoria"),
      fin: Yup.date()
        .typeError("La fecha de fin es obligatoria")
        .required("La fecha de fin es obligatoria")
        .when(
          "inicio",
          (inicio, yup) =>
            inicio &&
            yup.min(
              inicio,
              "La fecha de fin no puede ser anterior a la fecha de inicio"
            )
        ),
      horario: Yup.string().required("El horario es obligatorio"),
      inicioInscripciones: Yup.date()
        .typeError("La fecha de inicio de inscripciones es obligatoria")
        .required("La fecha de inicio de inscripciones es obligatoria"),

      finInscripciones: Yup.date()
        .typeError("La fecha de fin de inscripciones es obligatoria")
        .required("La fecha de fin de inscripciones es obligatoria")
        .when(
          "inicioInscripciones",
          (inicioInscripciones, yup) =>
            inicioInscripciones &&
            yup.min(
              inicioInscripciones,
              "La fecha de fin de inscripciones no puede ser anterior a la fecha de inicio de inscripciones"
            )
        ),
      plazas: Yup.number()
        .typeError("Debe ser un número válido")
        .min(0, "El número de plazas no puede ser negativo")
        .required("El número de plazas es obligatorio"),
      nhoras: Yup.number()
        .typeError("Debe ser un número válido")
        .min(0, "El número de horas no puede ser negativo")
        .required("El número de horas es obligatorio"),
      publicado: Yup.boolean().required(),
      presencial: Yup.boolean().required(),
      videoconferencia: Yup.boolean().required(),
      areaTematica: Yup.string().notRequired().nullable(),
      modeloCertificado: Yup.string().notRequired().nullable(),
      informacionParaElCertificado: Yup.string().notRequired().nullable(),
      personalDocente: Yup.string().notRequired().nullable(),
    });

    const { resetForm, handleSubmit, errors } = useForm<CursoEditar>({
      validationSchema: schema,
    });

    const { value: nombre } = useField("nombre");
    const { value: nexpediente } = useField("nexpediente");
    const { value: descripcionCorta } = useField("descripcionCorta");
    const { value: descripcion } = useField<string>("descripcion");
    const { value: imagen } = useField("imagen");
    const { value: inicio } = useField("inicio");
    const { value: fin } = useField("fin");
    const { value: horario } = useField("horario");
    const { value: inicioInscripciones } = useField("inicioInscripciones");
    const { value: finInscripciones } = useField("finInscripciones");
    const { value: plazas } = useField<number>("plazas");
    const { value: nhoras } = useField<number>("nhoras");
    const { value: publicado } = useField("publicado");
    const { value: presencial } = useField("presencial");
    const { value: videoconferencia } = useField("videoconferencia");
    const { value: areaTematica } = useField("areaTematica");
    const { value: modeloCertificado } = useField("modeloCertificado");
    const { value: informacionParaElCertificado } = useField<string>(
      "informacionParaElCertificado"
    );
    const { value: personalDocente } = useField<string>("personalDocente");

    // Image convert to base64.
    const beforeAvatarUpload: UploadProps["beforeUpload"] = (rawFile) => {
      if (
        rawFile.type !== "image/jpeg" &&
        rawFile.type !== "image/png" &&
        rawFile.type !== "image/gif"
      ) {
        ElMessage.error(t("imagen.errorFormat"));
        return false;
      }
      if (rawFile.size / 1024 / 1024 > 2) {
        ElMessage.error(t("imagen.errorSize"));
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(rawFile);
      reader.onload = function () {
        imagen.value = reader.result as string;
        imagenUrl.value = imagen.value as string;
        return true;
      };
      reader.onerror = function (error) {
        ElMessage.error("Error: " + error);
        return false;
      };

      return false;
    };

    return {
      Link,
      text,
      copy,
      copied,
      isSupported,
      errors,
      isCreate,
      inscripciones,
      nombre,
      nexpediente,
      descripcionCorta,
      descripcion,
      imagen,
      imagenUrl,
      inicio,
      fin,
      horario,
      inicioInscripciones,
      finInscripciones,
      plazas,
      nhoras,
      publicado,
      presencial,
      videoconferencia,
      url,
      cargaDetalle,
      cargaListadoProfesionales,
      crearCurso,
      actualizarCurso,
      beforeAvatarUpload,
      personalDocente,
      modeloCertificado,
      modelosCertificado: [
        {
          value: "IAM",
          label: "Modelo IAM",
        },
        {
          value: "PactoEstado",
          label: "Modelo Pacto de Estado",
        },
      ],
      submit: handleSubmit(async (values) => {
        if (isCreate) {
          crearCurso.value = true;
          try {
            await module
              .crear({
                curso: {
                  nombre: values.nombre,
                  nexpediente: values.nexpediente,
                  descripcionCorta: values.descripcionCorta,
                  descripcion: values.descripcion,
                  imagen: imagen.value ?? "",
                  inicio: values.inicio,
                  fin: values.fin,
                  horario: values.horario,
                  inicioInscripciones: values.inicioInscripciones,
                  finInscripciones: values.finInscripciones,
                  plazas: values.plazas,
                  nhoras: values.nhoras,
                  publicado: values.publicado,
                  presencial: values.presencial,
                  videoconferencia: values.videoconferencia,
                  areaTematica: values.areaTematica ?? "",
                  modeloCertificado: values.modeloCertificado ?? "",
                  informacionParaElCertificado:
                    values.informacionParaElCertificado ?? "",
                },
              })
              .then(() => {
                if (!module.errors)
                  Swal.fire({
                    text: t("formularios.curso.crear.mensaje.exito"),
                    icon: "success",
                    buttonsStyling: false,
                    confirmButtonText: t("dialogs.okButton"),
                    customClass: {
                      confirmButton: "btn btn-primary",
                    },
                  }).then(() => {
                    router.replace("/listados/cursos");
                  });
              });
          } catch (err) {
            Swal.fire({
              text: t("formularios.cursoDetalle.crear.error"),
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: t("dialogs.okButton"),
              customClass: {
                confirmButton: "btn btn-primary",
              },
            });
          } finally {
            crearCurso.value = false;
          }
        } else {
          actualizarCurso.value = true;
          try {
            await module
              .update({
                id: token as string,
                curso: {
                  nombre: values.nombre,
                  nexpediente: values.nexpediente,
                  descripcionCorta: values.descripcionCorta,
                  descripcion: values.descripcion,
                  imagen: imagen.value ?? "",
                  inicio: values.inicio,
                  fin: values.fin,
                  horario: values.horario,
                  inicioInscripciones: values.inicioInscripciones,
                  finInscripciones: values.finInscripciones,
                  plazas: values.plazas,
                  nhoras: values.nhoras,
                  publicado: values.publicado,
                  presencial: values.presencial,
                  videoconferencia: values.videoconferencia,
                  areaTematica: values.areaTematica ?? "",
                  modeloCertificado: values.modeloCertificado ?? "",
                  informacionParaElCertificado:
                    values.informacionParaElCertificado ?? "",
                  personalDocente: values.personalDocente ?? "",
                },
              })
              .then(() => {
                if (!module.errors)
                  Swal.fire({
                    text: t("formularios.curso.guardar.mensaje.exito"),
                    icon: "success",
                    buttonsStyling: false,
                    confirmButtonText: t("dialogs.okButton"),
                    customClass: {
                      confirmButton: "btn btn-primary",
                    },
                  }).then(() => {
                    //router.replace("/listados/cursos");
                  });
              });
          } catch (err) {
            Swal.fire({
              text: t("formularios.cursoDetalle.editar.error"),
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: t("dialogs.okButton"),
              customClass: {
                confirmButton: "btn btn-primary",
              },
            });
          } finally {
            actualizarCurso.value = false;
          }
        }
      }),
      eliminar: () => {
        Swal.fire({
          text: t("dialogs.eliminar.confirmacion"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("dialogs.okButton"),
          cancelButtonText: t("dialogs.cancelButton"),
          reverseButtons: true,
        }).then(({ isConfirmed }) => {
          if (!isConfirmed) return;

          cargaDetalle.value = true;
          return module
            .eliminar({
              elementId: token as string,
            })
            .then(() => {
              router.replace("/listados/cursos");
              ElMessage({
                message: t("dialogs.eliminar.exito"),
                type: "success",
              });
            })
            .catch((err) => {
              Swal.fire({
                text: t("dialogs.eliminar.error"),
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: t("dialogs.okButton"),
                customClass: {
                  confirmButton: "btn btn-primary",
                },
              });
              console.error(err);
            })
            .finally(() => {
              cargaDetalle.value = false;
            });
        });
      },
      colors: ["#99A9BF", "#F7BA2A", "#FF9900"],
      datosCurso,
      informacionParaElCertificado,
      areaTematica,
      areasTematicasValues: [
        {
          value: "Violencias contra la mujer",
          label: "Violencias contra la mujer",
        },
        {
          value: "Igualdad en la empresa",
          label: "Igualdad en la empresa",
        },
        {
          value: "Digital",
          label: "Digital",
        },
        {
          value: "Emprendimiento",
          label: "Emprendimiento",
        },
        {
          value: "Orientación y empleo",
          label: "Orientación y empleo",
        },
        {
          value: "Sensibilización en igualdad de género",
          label: "Sensibilización en igualdad de género",
        },
      ],
    };
  },
});
